import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { DateField, FlexColumn, FormModal, InputField, SelectField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useLeaveContracts from '@ubeya/shared/hooks/account/useLeaveContracts';
import { isValueBetweenNumberRange, required } from '@ubeya/shared/utils/form';
import moment from 'moment';

const Container = styled(FlexColumn)`
  gap: 16px;
  padding: 16px 28px;
`;

const FieldHint = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.grey4};
  margin-top: -8px;
`;

const formatDate = (value) => (value ? moment(value).format(API_DATE_FORMAT) : undefined);

// BE rejects future startDate (applyYearStartGrants fires at assign).
const isFutureDay = (day) => day && moment(day).startOf('day').isAfter(moment().startOf('day'));

// BE applies DEFAULT_EMPLOYMENT_RATE (100) when omitted.
const validateEmploymentRate = (value) => isValueBetweenNumberRange({ value, min: 0, max: 100, isRequired: false });

const AssignContractModal = ({ onClose, assignContract }) => {
  const { t } = useContext(ConfigContext);
  const { leaveContracts, isLoading } = useLeaveContracts();

  const contractOptions = useMemo(
    () =>
      (leaveContracts || [])
        .filter(({ isActive }) => isActive !== false)
        .map((contract) => ({ label: contract.name, value: contract.id })),
    [leaveContracts]
  );

  const onSubmit = useCallback(
    async (values) => {
      try {
        await assignContract({
          accountContractV2Id: values.accountContractV2Id,
          startDate: formatDate(values.startDate),
          // Number('') is NaN — omit when blank so BE default applies.
          employmentRate:
            Number.isFinite(Number(values.employmentRate)) && values.employmentRate !== ''
              ? Number(values.employmentRate)
              : undefined
        });
        onClose();
      } catch {
        // useOptimisticMutation already shows the toaster.
      }
    },
    [assignContract, onClose]
  );

  return (
    <FormModal
      onSubmit={onSubmit}
      size="small"
      requiredWarningFooter={t('missingRequiredFields')}
      onClose={onClose}
      title={t('assignLeaveContract')}
      confirmText={t('assign')}
      showCancelButton
      keepDirtyOnReinitialize
      disabledOnPristine={false}>
      <Container>
        <SelectField
          name="accountContractV2Id"
          title={t('leaveContract')}
          options={contractOptions}
          validate={required}
          required
          usePortal
          isMulti={false}
          clearable
          isLoading={isLoading}
        />
        <DateField
          name="startDate"
          title={t('startDate')}
          validate={required}
          required
          usePortal
          isOutsideRange={isFutureDay}
        />
        <InputField
          name="employmentRate"
          title={`${t('employmentRate')} (%)`}
          type="number"
          validate={validateEmploymentRate}
        />
        <FieldHint>{t('assignContract_employmentRate_hint')}</FieldHint>
      </Container>
    </FormModal>
  );
};

export default AssignContractModal;
